import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useColorModeValue } from './ui/color-mode';
import {
  Alert,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Progress,
  Spinner,
  Text,
  VStack,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster';
import {
  FaCheckCircle,
  FaCloudUploadAlt,
  FaExclamationTriangle,
  FaFileAlt,
  FaRobot,
  FaTrash,
  FaUserEdit,
} from 'react-icons/fa';
import { bandasAPI } from '../services/api';

const CONF_UMBRAL_ETIQUETAR = 0.9;

/**
 * Wizard para añadir páginas a una obra ya existente (manual o con auto-etiquetado).
 */
export default function BandaRepertorioAnadirPaginasWizard({
  isOpen,
  obraId,
  obraTitulo,
  onClose,
  onComplete,
}) {
  const pollingRef = useRef(null);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  const [step, setStep] = useState('choose'); // choose | upload | uploading | analyzing | applying | done | error
  const [modo, setModo] = useState(null); // 'manual' | 'auto'
  const [errorMsg, setErrorMsg] = useState('');
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState('');
  const [jobId, setJobId] = useState(null);
  const [jobEstado, setJobEstado] = useState(null);
  const [sugerencias, setSugerencias] = useState([]);
  const [resultado, setResultado] = useState(null);
  const [nuevasPaginasIds, setNuevasPaginasIds] = useState([]);
  const applyStartedRef = useRef(false);

  const dropZoneBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const dropZoneBorder = useColorModeValue('gray.300', 'whiteAlpha.300');
  const dropZoneHoverBg = useColorModeValue('purple.50', 'whiteAlpha.100');
  const fileRowHoverBg = useColorModeValue('gray.100', 'whiteAlpha.100');
  const choiceBorder = useColorModeValue('gray.200', 'whiteAlpha.300');
  const choiceHoverBg = useColorModeValue('purple.50', 'whiteAlpha.100');
  const choiceSelectedBg = useColorModeValue('purple.50', 'whiteAlpha.150');

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const resetState = useCallback(() => {
    stopPolling();
    setStep('choose');
    setModo(null);
    setErrorMsg('');
    setFiles([]);
    setUploadProgress('');
    setJobId(null);
    setJobEstado(null);
    setSugerencias([]);
    setResultado(null);
    setNuevasPaginasIds([]);
    applyStartedRef.current = false;
  }, [stopPolling]);

  useEffect(() => () => stopPolling(), [stopPolling]);
  useEffect(() => {
    if (!isOpen) resetState();
  }, [isOpen, resetState]);

  const progresoPct = useMemo(() => {
    if (!jobEstado?.totalPaginas) return 0;
    return Math.round(((jobEstado.progresoPaginas ?? 0) / jobEstado.totalPaginas) * 100);
  }, [jobEstado]);

  const addFiles = useCallback((newFiles) => {
    const ACCEPT_RE = /\.(pdf|png|jpe?g|webp)$/i;
    const snapshot = Array.from(newFiles || [])
      .filter((f) => ACCEPT_RE.test(f.name || '') || ACCEPT_RE.test(f.webkitRelativePath || ''))
      .sort((a, b) =>
        String(a.webkitRelativePath || a.name).localeCompare(
          String(b.webkitRelativePath || b.name),
          'es',
          { numeric: true },
        ),
      );
    if (!snapshot.length) return;
    setFiles((prev) => {
      const keyOf = (f) => `${f.webkitRelativePath || f.name}:${f.size}:${f.lastModified}`;
      const existing = new Set(prev.map(keyOf));
      const toAdd = snapshot.filter((f) => !existing.has(keyOf(f)));
      return toAdd.length ? [...prev, ...toAdd] : prev;
    });
  }, []);

  const removeFile = useCallback((idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const fetchEstado = useCallback(async (oid, jid) => {
    const r = await bandasAPI.getAutoEtiquetadoEstado(oid, jid);
    const data = r.data || {};
    setJobEstado(data.job || null);
    const pag = Array.isArray(data.paginas) ? data.paginas : [];
    setSugerencias(pag);
    return data;
  }, []);

  const startPolling = useCallback((oid, jid) => {
    stopPolling();
    pollingRef.current = setInterval(() => {
      fetchEstado(oid, jid).then((data) => {
        const estado = data?.job?.estado;
        if (estado === 'COMPLETADO' || estado === 'FALLIDO' || estado === 'CANCELADO') {
          stopPolling();
        }
      });
    }, 2000);
    fetchEstado(oid, jid);
  }, [fetchEstado, stopPolling]);

  const aplicarSugerencias = useCallback(async (oid, pags) => {
    setStep('applying');
    let aplicadas = 0;
    let revisables = 0;
    let errores = 0;
    let sumaConf = 0;
    let nConf = 0;

    for (const s of pags) {
      if (s.errorMensaje) {
        errores += 1;
        continue;
      }
      const conf = typeof s.confianza === 'number' ? s.confianza : 0;
      sumaConf += conf;
      nConf += 1;

      if (conf < CONF_UMBRAL_ETIQUETAR) {
        revisables += 1;
        continue;
      }

      try {
        // eslint-disable-next-line no-await-in-loop
        await bandasAPI.aplicarSugerenciaPagina(oid, s.paginaId, {
          estado: 'APLICADO',
          decision: {
            instrumentosVoces: s.instrumentosVoces || [],
            tituloObra: s.tituloObra ?? undefined,
            autor: s.autor ?? undefined,
            notasDirector: s.notasDirector ?? undefined,
            rotacion:
              typeof s.rotacionGrados === 'number' && (s.confianzaOrientacion ?? 0) >= 0.85
                ? s.rotacionGrados
                : undefined,
          },
        });
        aplicadas += 1;
      } catch {
        errores += 1;
      }
    }

    const promedioConfianza = nConf ? Math.round((sumaConf / nConf) * 100) : 0;
    setResultado({
      modo: 'auto',
      totalNuevas: pags.length || nuevasPaginasIds.length,
      aplicadas,
      revisables,
      errores,
      promedioConfianza,
      pctAplicadas: pags.length ? Math.round((aplicadas / pags.length) * 100) : 0,
    });
    setStep('done');
  }, [nuevasPaginasIds.length]);

  useEffect(() => {
    if (step !== 'analyzing' || !obraId || !jobEstado || !jobId) return;
    if (jobEstado.estado === 'COMPLETADO') {
      if (applyStartedRef.current) return;
      applyStartedRef.current = true;
      stopPolling();
      void (async () => {
        let pags = sugerencias;
        if (!pags.length) {
          try {
            const data = await fetchEstado(obraId, jobId);
            pags = Array.isArray(data?.paginas) ? data.paginas : [];
          } catch {
            pags = [];
          }
        }
        await aplicarSugerencias(obraId, pags);
      })();
    }
    if (jobEstado.estado === 'FALLIDO' || jobEstado.estado === 'CANCELADO') {
      stopPolling();
      setStep('error');
      setErrorMsg(jobEstado.errorMensaje || 'El análisis automático falló');
    }
  }, [step, jobEstado, jobId, sugerencias, obraId, stopPolling, aplicarSugerencias, fetchEstado]);

  const iniciarProceso = useCallback(async () => {
    if (!files.length || !obraId || !modo) return;
    setStep('uploading');
    setUploadProgress(`Subiendo archivos… (0/${files.length})`);
    setErrorMsg('');
    setResultado(null);
    setSugerencias([]);
    setJobEstado(null);

    try {
      const createdIds = [];
      const avisosTruncado = [];
      const BATCH = 30;
      for (let i = 0; i < files.length; i += BATCH) {
        setUploadProgress(`Subiendo archivos… (${Math.min(i + BATCH, files.length)}/${files.length})`);
        // eslint-disable-next-line no-await-in-loop
        const up = await bandasAPI.uploadObraPartituraArchivos(obraId, files.slice(i, i + BATCH));
        const batchIds = Array.isArray(up?.data?.paginasCreadasIds) ? up.data.paginasCreadasIds : [];
        createdIds.push(...batchIds);
        if (up?.data?.avisoTruncado) avisosTruncado.push(up.data.avisoTruncado);
      }
      if (avisosTruncado.length > 0) {
        toaster.create({
          title: 'Algunas páginas no se han subido',
          description: avisosTruncado[0],
          type: 'warning',
          duration: 12000,
          closable: true,
        });
      }
      if (!createdIds.length) {
        throw new Error('No se crearon páginas nuevas. Revisa los archivos subidos.');
      }
      setNuevasPaginasIds(createdIds);

      if (modo === 'manual') {
        setResultado({
          modo: 'manual',
          totalNuevas: createdIds.length,
        });
        setStep('done');
        return;
      }

      setStep('analyzing');
      const r = await bandasAPI.iniciarAutoEtiquetado(obraId, {
        reusarSiActivo: false,
        paginaIds: createdIds,
      });
      const jid = r.data?.jobId;
      if (!jid) throw new Error('No se pudo iniciar el análisis');
      setJobId(jid);
      startPolling(obraId, jid);
    } catch (e) {
      setStep('error');
      setErrorMsg(e?.response?.data?.message || e?.message || 'Error al añadir páginas');
    }
  }, [files, obraId, modo, startPolling]);

  const bloquearCierre = step === 'uploading' || step === 'analyzing' || step === 'applying';

  const handleCerrar = useCallback(() => {
    if (bloquearCierre) return;
    stopPolling();
    onClose?.();
  }, [bloquearCierre, stopPolling, onClose]);

  const handleCompletado = useCallback(() => {
    stopPolling();
    onComplete?.({
      modo: resultado?.modo || modo,
      openEtiquetado: true,
      resultado,
    });
    onClose?.();
  }, [stopPolling, onComplete, onClose, resultado, modo]);

  const tituloPaso = {
    choose: 'Añadir páginas',
    upload: 'Subir archivos',
    uploading: 'Subiendo archivos…',
    analyzing: 'Analizando páginas con IA',
    applying: 'Aplicando etiquetas…',
    done: 'Páginas añadidas',
    error: 'No se pudo completar',
  }[step] || 'Añadir páginas';

  return (
    <Dialog.Root
      open={isOpen}
      size="xl"
      scrollBehavior="inside"
      onOpenChange={(e) => {
        if (!e.open && !bloquearCierre) handleCerrar();
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content mx={4} maxH="90vh" display="flex" flexDirection="column" overflow="hidden">
            <Dialog.Header flexShrink={0}>
              {tituloPaso}
              {obraTitulo ? (
                <Text as="span" fontWeight="400" color="gray.500" fontSize="sm" ml={2}>
                  · {obraTitulo}
                </Text>
              ) : null}
            </Dialog.Header>
            {!bloquearCierre && <Dialog.CloseTrigger />}

            <Dialog.Body flex="1" minH={0} overflowY="auto">
              {step === 'choose' && (
                <VStack align="stretch" gap={4}>
                  <Text fontSize="sm" color="gray.600">
                    Elige cómo quieres etiquetar las páginas nuevas que vas a subir a esta obra.
                  </Text>
                  <Box
                    borderWidth="2px"
                    borderColor={modo === 'manual' ? 'purple.400' : choiceBorder}
                    bg={modo === 'manual' ? choiceSelectedBg : undefined}
                    rounded="xl"
                    p={4}
                    cursor="pointer"
                    _hover={{ bg: choiceHoverBg, borderColor: 'purple.300' }}
                    onClick={() => setModo('manual')}
                  >
                    <HStack align="start" gap={3}>
                      <Icon as={FaUserEdit} color="purple.500" boxSize={5} mt={0.5} />
                      <Box>
                        <Text fontWeight="700">Subir y etiquetar manualmente</Text>
                        <Text fontSize="sm" color="gray.600" mt={1}>
                          Subes las páginas y luego las etiquetas tú en el editor de páginas.
                        </Text>
                      </Box>
                    </HStack>
                  </Box>
                  <Box
                    borderWidth="2px"
                    borderColor={modo === 'auto' ? 'purple.400' : choiceBorder}
                    bg={modo === 'auto' ? choiceSelectedBg : undefined}
                    rounded="xl"
                    p={4}
                    cursor="pointer"
                    _hover={{ bg: choiceHoverBg, borderColor: 'purple.300' }}
                    onClick={() => setModo('auto')}
                  >
                    <HStack align="start" gap={3}>
                      <Icon as={FaRobot} color="purple.500" boxSize={5} mt={0.5} />
                      <Box>
                        <Text fontWeight="700">Subir y etiquetar automáticamente</Text>
                        <Text fontSize="sm" color="gray.600" mt={1}>
                          La IA analiza solo las páginas nuevas y aplica etiquetas con alta confianza. Luego conviene revisarlas.
                        </Text>
                      </Box>
                    </HStack>
                  </Box>
                </VStack>
              )}

              {step === 'upload' && (
                <VStack align="stretch" gap={4}>
                  <Alert.Root status="info" borderRadius="md" fontSize="sm">
                    <Alert.Indicator />
                    <Text>
                      Sube PDF o imágenes. Se añadirán al final de «{obraTitulo || 'esta obra'}»
                      {modo === 'auto' ? ' y se analizarán con IA.' : '.'}
                    </Text>
                  </Alert.Root>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.png,.jpg,.jpeg,.webp"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const snapshot = Array.from(e.target.files || []);
                      e.target.value = '';
                      if (snapshot.length) addFiles(snapshot);
                    }}
                  />
                  <input
                    ref={folderInputRef}
                    type="file"
                    multiple
                    webkitdirectory=""
                    directory=""
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const snapshot = Array.from(e.target.files || []);
                      e.target.value = '';
                      if (snapshot.length) addFiles(snapshot);
                    }}
                  />
                  <Box
                    border="2px dashed"
                    borderColor={dropZoneBorder}
                    borderRadius="lg"
                    bg={dropZoneBg}
                    p={8}
                    textAlign="center"
                    cursor="pointer"
                    _hover={{ borderColor: 'purple.400', bg: dropZoneHoverBg }}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Icon w={10} h={10} color="purple.400" mb={2} asChild><FaCloudUploadAlt /></Icon>
                    <Text fontSize="sm" fontWeight="600" color="purple.600">
                      Arrastra archivos aquí o haz clic para seleccionar
                    </Text>
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      PDF, PNG, JPG, WebP
                    </Text>
                  </Box>
                  <HStack justify="center" gap={3} flexWrap="wrap">
                    <Button
                      size="sm"
                      variant="outline"
                      colorPalette="purple"
                      onClick={(e) => {
                        e.stopPropagation();
                        folderInputRef.current?.click();
                      }}
                    >
                      Seleccionar carpeta
                    </Button>
                  </HStack>
                  {files.length > 0 && (
                    <VStack align="stretch" gap={1} maxH="200px" overflowY="auto">
                      {files.map((f, idx) => (
                        <HStack
                          key={`${f.webkitRelativePath || f.name}-${idx}`}
                          px={3}
                          py={2}
                          rounded="md"
                          _hover={{ bg: fileRowHoverBg }}
                          justify="space-between"
                        >
                          <HStack gap={2} minW={0}>
                            <Icon as={FaFileAlt} color="gray.400" flexShrink={0} />
                            <Text fontSize="sm" lineClamp={1}>
                              {f.webkitRelativePath || f.name}
                            </Text>
                          </HStack>
                          <Button
                            size="xs"
                            variant="ghost"
                            colorPalette="red"
                            aria-label="Quitar"
                            onClick={() => removeFile(idx)}
                          >
                            <FaTrash />
                          </Button>
                        </HStack>
                      ))}
                    </VStack>
                  )}
                </VStack>
              )}

              {step === 'uploading' && (
                <Flex direction="column" align="center" gap={3} py={10}>
                  <Spinner size="lg" color="purple.500" />
                  <Text fontSize="sm" color="gray.600">{uploadProgress || 'Subiendo…'}</Text>
                </Flex>
              )}

              {step === 'analyzing' && (
                <VStack align="stretch" gap={4} py={4}>
                  <Text fontSize="sm" color="gray.600" textAlign="center">
                    Analizando las páginas nuevas con IA…
                  </Text>
                  <Progress.Root value={progresoPct} colorPalette="purple" size="md" striped animated>
                    <Progress.Track>
                      <Progress.Range />
                    </Progress.Track>
                  </Progress.Root>
                  <Text fontSize="xs" color="gray.500" textAlign="center">
                    {jobEstado
                      ? `${jobEstado.progresoPaginas ?? 0} / ${jobEstado.totalPaginas ?? '…'} páginas`
                      : 'Iniciando…'}
                  </Text>
                </VStack>
              )}

              {step === 'applying' && (
                <Flex direction="column" align="center" gap={3} py={10}>
                  <Spinner size="lg" color="purple.500" />
                  <Text fontSize="sm" color="gray.600">Aplicando etiquetas de alta confianza…</Text>
                </Flex>
              )}

              {step === 'done' && resultado?.modo === 'manual' && (
                <Alert.Root status="warning" borderRadius="md">
                  <Alert.Indicator asChild><FaExclamationTriangle /></Alert.Indicator>
                  <Box>
                    <Text fontWeight="700">
                      Se han añadido {resultado.totalNuevas} página{resultado.totalNuevas !== 1 ? 's' : ''}
                    </Text>
                    <Text fontSize="sm" mt={2}>
                      Debes etiquetar las páginas nuevas. Al continuar se abrirá la pantalla de páginas y etiquetado.
                    </Text>
                  </Box>
                </Alert.Root>
              )}

              {step === 'done' && resultado?.modo === 'auto' && (
                <VStack align="stretch" gap={3}>
                  <Alert.Root status="success" borderRadius="md">
                    <Alert.Indicator asChild><FaCheckCircle /></Alert.Indicator>
                    <Box>
                      <Text fontWeight="700">
                        Se han añadido {resultado.totalNuevas} página{resultado.totalNuevas !== 1 ? 's' : ''}
                      </Text>
                      <Text fontSize="sm" mt={2}>
                        Etiquetado automático aplicado en {resultado.aplicadas} de {resultado.totalNuevas} páginas
                        ({resultado.pctAplicadas}% con confianza ≥ {Math.round(CONF_UMBRAL_ETIQUETAR * 100)}%).
                      </Text>
                      <Text fontSize="sm" mt={1}>
                        Confianza media de la IA: <strong>{resultado.promedioConfianza}%</strong>
                        {resultado.revisables > 0
                          ? ` · ${resultado.revisables} página${resultado.revisables !== 1 ? 's' : ''} pendientes de revisar`
                          : ''}
                        {resultado.errores > 0
                          ? ` · ${resultado.errores} con error`
                          : ''}
                      </Text>
                    </Box>
                  </Alert.Root>
                  <Alert.Root status="warning" borderRadius="md">
                    <Alert.Indicator asChild><FaExclamationTriangle /></Alert.Indicator>
                    <Text fontSize="sm">
                      Revisa bien el etiquetado de las páginas nuevas antes de dar la obra por lista.
                      Al continuar se abrirá el editor de páginas.
                    </Text>
                  </Alert.Root>
                </VStack>
              )}

              {step === 'error' && (
                <Alert.Root status="error" borderRadius="md">
                  <Alert.Indicator />
                  <Text fontSize="sm">{errorMsg || 'Error desconocido'}</Text>
                </Alert.Root>
              )}
            </Dialog.Body>

            <Dialog.Footer flexShrink={0}>
              {step === 'choose' && (
                <>
                  <Button variant="ghost" mr={3} onClick={handleCerrar}>Cancelar</Button>
                  <Button colorPalette="purple" disabled={!modo} onClick={() => setStep('upload')}>
                    Siguiente
                  </Button>
                </>
              )}
              {step === 'upload' && (
                <>
                  <Button variant="ghost" mr={3} onClick={() => setStep('choose')}>Atrás</Button>
                  <Button colorPalette="purple" disabled={!files.length} onClick={iniciarProceso}>
                    {modo === 'auto' ? 'Subir y analizar' : 'Subir páginas'}
                  </Button>
                </>
              )}
              {step === 'done' && (
                <Button colorPalette="purple" onClick={handleCompletado}>
                  Ir a páginas y etiquetado
                </Button>
              )}
              {step === 'error' && (
                <Button variant="ghost" onClick={handleCerrar}>Cerrar</Button>
              )}
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
